import { AxiosResponse } from 'axios'
import {
  Guid,
  LoginViewModel,
  FedLoginViewModel,
  FederationViewModel,
  AuthStatus,
  AuthResult,
  SendCodeViewModel,
  SendFedCodeViewModel,
  EmptyApiResult,
  VerifyCodeViewModel,
} from '../api'
import { AuthAction } from 'contexts/AuthContext'
import { useApiClient } from './useApiClient'
import { createFormDataFromModel } from 'utils'
import { useAuth } from '.'

export const useAuthApi = () => {
  const apiClient = useApiClient()

  const login = (values: LoginViewModel) => {
    const formData = createFormDataFromModel(values)

    return apiClient.request<void, AxiosResponse<AuthResult>>({
      url: '/api/login',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const checkIsFederated = (values: FedLoginViewModel) => {
    const formData = createFormDataFromModel(values)
    return apiClient.request<void, AxiosResponse<FederationViewModel>>({
      url: `/api/auth/federation/check`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const sendFedCode = (values: SendFedCodeViewModel | null) => {
    return apiClient.request<void, AxiosResponse<AuthResult>>({
      url: '/api/auth/federation/sendCode',
      method: 'post',
      data: values,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    })
  }

  const sendVerificationCode = (values: SendCodeViewModel) => {
    const formData = createFormDataFromModel(values)

    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: '/api/auth/2fa/sendCode',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const submitVerificationCode = (values: VerifyCodeViewModel) => {
    const formData = createFormDataFromModel(values)
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: '/api/auth/2fa/verifyCode',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const { agencies: agencies, authorizedAgencies: authorizedAgencies } = useAuth()

  const getCurrentSession = (
    dispatch?: (action: AuthAction) => void,
    postAuthRedirectUri?: string,
  ) => {
    return apiClient
      .request<void, AxiosResponse<AuthStatus>>({
        url: '/api/auth',
        method: 'get',
        withCredentials: true,
      })
      .then((response) => {
        if (response === null) return

        if (response.status === 200 && response.data.isAuthenticated) {
          dispatch &&
            dispatch({
              type: 'SetSession',
              payload: {
                user: response.data.user,
                loginSessionTimeoutInMinutes:
                  response.data.loginSessionTimeoutInMinutes,
                lastApiCall: Date.now(),
                authorizedAgencies: authorizedAgencies,
                agencies: agencies
              },
            })
          if (postAuthRedirectUri) window.location.assign(postAuthRedirectUri)
        }

        return response
      })
  }

  const logout = () => {
    return apiClient.request<void, AxiosResponse<void>>({
      url: '/api/logout',
      method: 'get',
      withCredentials: true,
    })
  }

  return {
    login,
    logout,
    checkIsFederated,
    sendFedCode,
    getCurrentSession,
    sendVerificationCode,
    submitVerificationCode,
  }
}
